import React, { FC } from 'react';
import { withRouter, RouteComponentProps } from 'react-router';
import { compose } from 'recompose';

import { SearchSortDirections, SearchSortFields } from 'api';
import * as paths from 'paths';
import { GroupListFollow, GroupListOwner, Layouts } from 'components';
import { withFollowed, IWithFollowed } from 'providers';
import { useCurrentUser } from 'hooks';

import { FilterSelect, LatestGroups, QueryList } from '../components/Index';
import { getModeratedGroups } from '../helpers';

interface IMatchParams {
  department_id: string;
  location_id: string;
}

interface IPropsEnhanced extends IWithFollowed, RouteComponentProps<IMatchParams> {}

const RESULTS_FROM = 0;
const SIZE = 10;

const enhancers = compose<IPropsEnhanced, {}>(withFollowed('groups'), withRouter);

const GroupsIndex: FC<IPropsEnhanced> = props => {
  const currentUser = useCurrentUser();
  const { groups, match } = props;

  const isIndex = match.path === paths.groups;
  const isOwned = match.path === paths.groupsOwned;
  const isFollowing = match.path === paths.groupsFollowing;
  const isAll = match.path === paths.groupsAll;
  const isDepartmentFilter = match.path === paths.groupsDepartment;
  const isLocationFilter = match.path === paths.groupsLocation;

  const filterVariables = {
    results_from: RESULTS_FROM,
    size: SIZE,
  };

  if (isIndex) {
    const variables = { ...filterVariables, auto_exclude: true };
    return (
      <Layouts.App2Column
        sidebar={<FilterSelect />}
        main={
          <Layouts.AppMain2Column
            desktopSplit="9/3"
            left={<QueryList emptyStateName="groupsRecommended" variables={variables} />}
            right={<LatestGroups />}
          />
        }
      />
    );
  }

  let main = null;

  if (isOwned) {
    const ownedGroups = getModeratedGroups(currentUser, groups);
    main = <GroupListOwner emptyStateName="groupsOwned" groups={ownedGroups} />;
  }

  if (isFollowing) {
    main = <GroupListFollow emptyStateName="groupsFollowed" disableLoadMore groups={groups} />;
  }

  if (isAll) {
    const variables = {
      ...filterVariables,
      auto_exclude: false,
      sort: SearchSortFields.Title,
      sort_direction: SearchSortDirections.Asc,
    };
    main = <QueryList variables={variables} />;
  }

  if (isDepartmentFilter) {
    const { department_id } = match.params;
    const variables = {
      ...filterVariables,
      auto_exclude: false,
      required_department_id: department_id,
    };

    main = <QueryList emptyStateName="groupsDepartments" variables={variables} />;
  }

  if (isLocationFilter) {
    const { location_id } = match.params;
    const variables = {
      ...filterVariables,
      auto_exclude: false,
      required_location_id: location_id,
    };

    main = <QueryList emptyStateName="groupsLocations" variables={variables} />;
  }

  return (
    <Layouts.App2Column sidebar={<FilterSelect />} main={<Layouts.AppMain1Column main={main} />} />
  );
};

export default enhancers(GroupsIndex);
